import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { moviesApi } from "@/api/movies";
import type { CollectionPart } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { PageHeader } from "@/components/ui/PageHeader";
import { Skeleton } from "@/components/ui/Skeleton";
import { Status } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";

/**
 * `/movies/collections/:id` — per-collection detail. Renders every part
 * with its in-library state and a per-part Add button when missing.
 * Issue #49.
 */
export function MovieCollectionDetail() {
  const { id } = useParams({ from: "/movies/collections/$id" });
  const tmdbId = Number(id);
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ["collection", tmdbId],
    queryFn: () => moviesApi.collections.get(tmdbId),
  });

  const monitorMut = useMutation({
    mutationFn: (monitored: boolean) =>
      moviesApi.collections.monitor(tmdbId, monitored),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["collection", tmdbId] });
      qc.invalidateQueries({ queryKey: ["collections"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Update failed", "error"),
  });

  const addAllMut = useMutation({
    mutationFn: () => moviesApi.collections.addAll(tmdbId),
    onSuccess: (r) => {
      toast(
        `Added ${r.added}, skipped ${r.skipped}`,
        r.added > 0 ? "success" : "info",
      );
      qc.invalidateQueries({ queryKey: ["collection", tmdbId] });
      qc.invalidateQueries({ queryKey: ["collections"] });
      qc.invalidateQueries({ queryKey: ["movies"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Bulk-add failed", "error"),
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton.Block height="3rem" width="22rem" />
        <Skeleton.Cards count={6} cols={3} />
      </div>
    );
  }
  if (!data) {
    return <p className="text-sm text-foreground/55">Not found.</p>;
  }
  const { collection, parts } = data;
  const inLibraryCount = parts.filter((p) => p.in_library).length;
  const missingCount = parts.length - inLibraryCount;

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Collection"
        title={collection.name}
        description={collection.overview ?? undefined}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => monitorMut.mutate(!collection.monitored)}
              loading={monitorMut.isPending}
            >
              {collection.monitored ? "Unmonitor" : "Monitor"}
            </Button>
            <Button
              onClick={() => addAllMut.mutate()}
              loading={addAllMut.isPending}
              disabled={missingCount === 0}
            >
              Add all ({missingCount})
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <Status
          variant="chip"
          tone={collection.monitored ? "accent" : "neutral"}
        >
          {collection.monitored ? "monitored" : "not monitored"}
        </Status>
        <Status variant="chip" tone="neutral">
          {inLibraryCount} / {parts.length} in library
        </Status>
      </div>

      {parts.length === 0 ? (
        <EmptyState
          glyph="film"
          title="No parts known"
          description="TMDB hasn't returned any parts for this collection yet. Try refreshing later."
        />
      ) : (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {parts.map((p) => (
            <PartCard key={p.tmdb_id} part={p} collectionId={tmdbId} />
          ))}
        </div>
      )}
    </div>
  );
}

function PartCard({
  part,
  collectionId,
}: {
  part: CollectionPart;
  collectionId: number;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();

  const addMut = useMutation({
    mutationFn: () => moviesApi.add({ tmdb_id: part.tmdb_id, monitored: true }),
    onSuccess: (m) => {
      toast(`Added ${m.title}`, "success");
      qc.invalidateQueries({ queryKey: ["collection", collectionId] });
      qc.invalidateQueries({ queryKey: ["movies"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Add failed", "error"),
  });

  const cardInner = (
    <>
      <div className="relative">
        {part.poster_url ? (
          <img
            src={part.poster_url}
            alt=""
            className="aspect-[2/3] w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="grid aspect-[2/3] place-items-center bg-surface-2 text-xs text-foreground/35">
            no poster
          </div>
        )}
        <div className="absolute right-2 top-2">
          <Status
            variant="chip"
            tone={part.in_library ? "good" : "accent"}
          >
            {part.in_library ? "in library" : "wanted"}
          </Status>
        </div>
      </div>
      <div className="p-3">
        <div className="line-clamp-2 font-display text-sm font-medium leading-tight tracking-display-tight">
          {part.title}
        </div>
        <div className="mt-1 flex items-center justify-between font-mono text-[11px] text-foreground/55">
          <span className="tabular-nums">{part.year ?? "—"}</span>
        </div>
      </div>
    </>
  );

  return (
    <div className="overflow-hidden rounded-md border border-line bg-surface/30 transition hover:border-line-strong">
      {part.in_library && part.movie_id != null ? (
        <Link
          to="/movies/$id"
          params={{ id: String(part.movie_id) }}
          className="block"
        >
          {cardInner}
        </Link>
      ) : (
        <div>{cardInner}</div>
      )}
      {!part.in_library && (
        <div className="px-3 pb-3">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => addMut.mutate()}
            loading={addMut.isPending}
          >
            Add
          </Button>
        </div>
      )}
    </div>
  );
}
